import { Canvas3DGeometry, type Canvas3DGeometryOptions } from './canvas3dGeometry';

/**
 * Phase 6d — CANVAS INTERACTION: the raycast picking / hover / select /
 * context-menu / keyboard layer of legacy canvas.view3d.js, ported FAITHFULLY
 * over the 6b/6c Canvas3DGeometry. Source blocks (canvas.view3d.js):
 *
 *  - onPointerMove                 :784-790   (NDC pointer + hover dispatch)
 *  - getAccessoryTooltip           :826-859   (per-type tooltip templates,
 *                                              #accessory-tooltip-for-canvas,
 *                                              offset +19px x — §3.2.7/parity 69)
 *  - _tooltipEl                    :826-830   (preview-local tooltip el)
 *  - onSectionAction               :876-1095  (THE picking decision tree —
 *                                              raycast priority airflow icons →
 *                                              section cubes → accessory shapes →
 *                                              lugs; click select/deselect +
 *                                              Ctrl multi; dblclick defers;
 *                                              parity 65-73)
 *  - onRightClick                  :1096-1175 (hit typing for context menus)
 *  - openAccessoryMenu/openSectionMenu/closeContextMenu :5742-5770 (parity 71)
 *  - keyDown                       :349-378   (Enter/←→↑↓ nav — parity 75)
 *  - selectOrdeselectSectiononKeyDown :379-417 (Ctrl keyboard multi-select)
 *
 * §3.5 DOM couplings are re-homed as option hooks; every programmatic
 * `$().click()` into the left panel maps to its §13 row (see the
 * Canvas3DInteractionOptions docs — rows 4/6/8/9/10/15 senders live HERE).
 *
 * [NEG]s honored:
 *  - #3  getSectionTooltip — dead (TypeError on first call); NOT ported.
 *  - #10 `is_selected` implicit window global — scoped instance field.
 *  - #108 components/*\/view/tooltip.js (.hover-tooltip fillers) — dead in
 *        legacy (zero requires); the live canvas tooltip is ONLY
 *        #accessory-tooltip-for-canvas. The .hover-tooltip mounts stay inert.
 *  - G34 note: the `dimensionColor` vars of onSectionAction are computed but
 *        never applied in legacy (dmount-frame recolors commented out) — not
 *        ported.
 */

// §3.2.1 module constants used by the picking layer (canvas.view3d:41-48 —
// duplicated verbatim from the geometry module scope; values are the table's).
const sectionMaterialColorHovered = 0x9bb3d7;
const sectionMaterialColorActive = 0x3867af;
const wireframeMaterialColor = 0x9f9e9e;
const wireframeMaterialColorActive = 0x3867af;
const wireframeMaterialColorHovered = 0xb0bcd0;

export interface Canvas3DInteractionOptions extends Canvas3DGeometryOptions {
  /** viewdata.current_tab read (`#show_accessories.active` probe re-home). */
  getCurrentTab(): string;
  /**
   * `.main-rotation.rotate` rating-mode guard (§3.2.6 keyDown + dblclick) —
   * §13 row 21 boardMode slice read.
   */
  isRatingModeActive(): boolean;
  /**
   * Direct viewdata writes (the canvas paths that bypass `select:section`:
   * already-active deselect branch, empty-space multi-clear, keyboard
   * Ctrl-deselect) — `stateStorage viewdata.set({...})` re-home.
   */
  onViewdataSet(payload: {
    currentTab: 'ahu' | 'sections';
    currentSectionId: number | null;
    selectedSectionIds: number[];
  }): void;
  /** §13 row 4 — `$('#show_accessories').click()` → setTab('accessories'). */
  onShowAccessoriesTab(): void;
  /** §13 row 8 — `li#forms_accessory_<id> … i.accessory-expand` click. */
  onExpandAccessoryRow(accessoryId: number): void;
  /** §13 row 9 — `input[data-subtab="mounting"] + label i` click (ACCESS_DOOR
   * auto path; the 300 ms defer stays in the canvas — observable behavior). */
  onOpenAccessoryMountingSubtab(accessoryId: number): void;
  /**
   * §13 row 10 — `#forms_accessory_<id> label.active-mode` click. That label is
   * the accessory form's "Select" button, so this opens the accessory's
   * SELECTION dialog; 6d read it as the mounting popup.
   */
  onOpenAccessorySelector(accessoryId: number): void;
  /**
   * §13 rows 6/7 — `$('#show_rating:not(.invisible-tab)[:not(.active)]')`
   * click → openRating. `requireInactive` = the Enter-key variant's extra
   * `:not(.active)`.
   */
  onOpenRating(requireInactive: boolean): void;
  /**
   * The two accessory-row collapse click chains of onSectionAction
   * (`#forms_accessories > div.accessories[:not(.active)] … input:checked`
   * clicks): 'others' = rows of sections other than the CURRENT one,
   * 'all' = every checked row.
   */
  onCollapseAccessoryRows(scope: 'others' | 'all'): void;
  /** §13 row 15 — `li.tunnel_li.tunnel_li_0 input.subtab:not(:checked)` click. */
  onOpenFirstTunnelAccordion(): void;
  /**
   * Airflow-arrow click → `tunnels.get(id).toggle_airflow_direction()`
   * (model/tunnel.js:203-230 — PATCH ahu/tunnels/<id> {airflow_direction_id},
   * §20.3 #21; loading chrome + post-success re-render are the caller's).
   */
  onToggleAirflowDirection(tunnelId: number): void;
  /** ADP_GAGE tooltip mfg names (fn['current.cms.adp_gage'] →
   * cms adp_gage.manufacturer map; undefined pre-resolve/missing id renders
   * the literal "undefined" exactly like the legacy bare map lookup). */
  getAdpGageMfgName(mfgId: number | string): string | undefined;
}

export class Canvas3DInteraction extends Canvas3DGeometry {
  getCurrentTab: () => string;
  isRatingModeActive: () => boolean;
  onViewdataSet: Canvas3DInteractionOptions['onViewdataSet'];
  onShowAccessoriesTab: () => void;
  onExpandAccessoryRow: (accessoryId: number) => void;
  onOpenAccessoryMountingSubtab: (accessoryId: number) => void;
  onOpenAccessorySelector: (accessoryId: number) => void;
  onOpenRating: (requireInactive: boolean) => void;
  onCollapseAccessoryRows: (scope: 'others' | 'all') => void;
  onOpenFirstTunnelAccordion: () => void;
  onToggleAirflowDirection: (tunnelId: number) => void;
  getAdpGageMfgName: (mfgId: number | string) => string | undefined;

  /** Ctrl keyboard-chain anchor (§3.2.6 keyDown). */
  last_pressed_section: any = null;
  /** [NEG]#10 — legacy `is_selected = true` implicit window global, scoped. */
  is_selected = false;
  /** onSectionAction `this.selected` stamp (canvas.view3d:1010). */
  selected: any = null;

  constructor(options: Canvas3DInteractionOptions) {
    super(options);
    this.getCurrentTab = options.getCurrentTab;
    this.isRatingModeActive = options.isRatingModeActive;
    this.onViewdataSet = options.onViewdataSet;
    this.onShowAccessoriesTab = options.onShowAccessoriesTab;
    this.onExpandAccessoryRow = options.onExpandAccessoryRow;
    this.onOpenAccessoryMountingSubtab = options.onOpenAccessoryMountingSubtab;
    this.onOpenAccessorySelector = options.onOpenAccessorySelector;
    this.onOpenRating = options.onOpenRating;
    this.onCollapseAccessoryRows = options.onCollapseAccessoryRows;
    this.onOpenFirstTunnelAccordion = options.onOpenFirstTunnelAccordion;
    this.onToggleAirflowDirection = options.onToggleAirflowDirection;
    this.getAdpGageMfgName = options.getAdpGageMfgName;
  }

  /**
   * §3.2.6 interaction listeners (canvas.view3d initialize :215-218, :239-257,
   * :325) — registered HERE with full cleanup (R8: the legacy leaked its
   * window contextmenu/keydown listeners on every reset; React must not).
   * The #forms-main-container keydown stopPropagation guard is the 6b
   * AhuCanvas's (already live).
   */
  attachInteraction(): () => void {
    const detachHover = this.attachHoverInteraction();
    const dom = this.renderer.domElement;
    const onClick = (e: Event) => this.onSectionAction(e as MouseEvent);
    const onContextMenu = (event: MouseEvent) => {
      this.closeContextMenu();
      // parity 11/71 — view permission suppresses the canvas menus.
      if (document.body.classList.contains('ahu-view-permission')) return;
      if (!(event.target instanceof Element) || event.target.tagName !== 'CANVAS') {
        this.closeContextMenu();
        return;
      }
      event.preventDefault();
      const result = this.onRightClick(event);
      if (!result) return;
      if (result.type === 'accessory') this.openAccessoryMenu(result);
      else if (result.type === 'section') this.openSectionMenu(result);
      else this.closeContextMenu();
    };
    const onWindowClick = () => this.closeContextMenu();
    const onKeyDown = (e: KeyboardEvent) => this.keyDown(e);

    dom.addEventListener('click', onClick);
    dom.addEventListener('dblclick', onClick);
    window.addEventListener('contextmenu', onContextMenu);
    window.addEventListener('click', onWindowClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      detachHover();
      dom.removeEventListener('click', onClick);
      dom.removeEventListener('dblclick', onClick);
      window.removeEventListener('contextmenu', onContextMenu);
      window.removeEventListener('click', onWindowClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }

  /**
   * Preview-only interaction (accessory selector mini-3D AHU tab —
   * useAccessoryPreviewCanvas.ts / createAccessoryPreviewCanvas below): hover
   * tooltip (getAccessoryTooltip) + the `need_update` pump that lets
   * OrbitControls' drag-rotate actually repaint. canvas3d.ts's render() loop
   * gates on `need_update` (`if (!this.need_update && !autoRotate) return`),
   * and OrbitControls itself never sets it — on the LIVE board canvas it's
   * onSectionAction('hover', …) setting `this.need_update = true` on every
   * pointermove tick (the same events a drag naturally fires) that keeps the
   * render loop pumping so `_controls.update()` gets a chance to read the
   * dragged camera state. The legacy accessory-selector preview canvas is
   * literally the same canvas.view3d.js View class re-parented, so it got
   * this coupling for free; the React preview was built on bare
   * Canvas3DGeometry with no picking layer at all, so neither hover nor
   * drag-rotate did anything. This is the scoped fix: only pointermove/
   * mouseout, scoped to THIS canvas's own dom element (not window).
   * Click/dblclick/contextmenu/keydown stay UNWIRED — onSectionAction /
   * onRightClick / keyDown all early-return non-hover events once
   * `previewAccessoryId` is set, so there is nothing for them to do, and
   * wiring them would fight the live canvas over the shared
   * #accessory-context-menu / #section-context-menu DOM singletons and the
   * window-level listeners.
   */
  attachHoverInteraction(): () => void {
    const dom = this.renderer.domElement;
    const onPointerMove = (e: Event) => this.onPointerMove(e as MouseEvent);
    dom.addEventListener('pointermove', onPointerMove);
    dom.addEventListener('mouseout', onPointerMove);
    return () => {
      dom.removeEventListener('pointermove', onPointerMove);
      dom.removeEventListener('mouseout', onPointerMove);
    };
  }

  /** onPointerMove (canvas.view3d:784-790). */
  onPointerMove(event: MouseEvent): void {
    this.renderer.domElement.classList.remove('cursor-pointer');
    // Normalized device coordinates (-1..+1) for both components.
    this.pointer.x = ((event.clientX - this.el.getBoundingClientRect().x) / this.el.offsetWidth) * 2 - 1;
    this.pointer.y = -((event.clientY - this.el.getBoundingClientRect().y) / this.el.offsetHeight) * 2 + 1;
    this.onSectionAction('hover', event);
  }

  /** _tooltipEl (canvas.view3d:826-830) — preview-local vs board tooltip. */
  _tooltipEl(): HTMLElement | null {
    return this.previewAccessoryId != null
      ? (this.el.parentElement?.querySelector('.accessory-tooltip-for-canvas') as HTMLElement | null)
      : document.getElementById('accessory-tooltip-for-canvas');
  }

  /**
   * getAccessoryTooltip (canvas.view3d:831-859) — per-type templates (W×H×D,
   * mfg/series/voltage/amp from `<type>_params` JSON, ADP_GAGE mfg via CMS
   * names), positioned offsetX+19/offsetY (parity 69). The AIRFLOW pseudo-type
   * carries the OA/SA/RA/EA label + "Click to reverse direction" (parity 73).
   */
  getAccessoryTooltip(accessory: any, mouseEvent: MouseEvent): void {
    const tooltip = this._tooltipEl();
    if (!tooltip) return; // shell node absent — defensive (legacy would throw)
    const width = Number(accessory.width || accessory.width_calc || 0).toFixed(2);
    const aw_width = Number(accessory.aw_width || 0).toFixed(2);
    const aw_height = Number(accessory.aw_height || 0).toFixed(2);
    const height = Number(accessory.height || accessory.height_calc || 0).toFixed(2);
    const depth = Number(accessory.depth || 0).toFixed(2);
    const params = accessory[`${accessory.name.toLowerCase()}_params`]
      ? JSON.parse(accessory[`${accessory.name.toLowerCase()}_params`])
      : {};
    const accessories: Record<string, string> = {
      AIRFLOW: accessory.airflowLabel
        ? `<span>${accessory.airflowLabel}</span><span class="extra-info">Click to reverse direction</span>`
        : 'Click to reverse direction',
      ACCESS_DOOR: `${accessory.label}<span class="extra-info">${width}Wx${height}H</span>`,
      OPENING: `${accessory.label}<span class="extra-info">${width}Wx${height}H</span>`,
      DOG_HOUSE: `${accessory.label}<span class="extra-info">${width}Wx${height}Hx${depth}D</span>`,
      ADP_GAGE: `${accessory.label}<span class="extra-info">${params?.mfg_id ? `${this.getAdpGageMfgName(params.mfg_id)}` : ''}${params?.model ? `, ${params.model}` : ''}</span>`,
      // COMBO LOUVER — A2.2 (canvas.view3d.js:855): a combo suppresses BOTH
      // extra-info spans, because mfg_name/series_name live per-face in the
      // shared identity and aw_width/aw_height are null on the parent record
      // (they would render "0.00Wx0.00H"). Label only — the server label is
      // already "Combo Louver".
      LOUVER: accessory.is_combo
        ? `${accessory.label}`
        : `${accessory.label}<span class="extra-info">${params?.mfg_name ? `${params.mfg_name}` : ''}${params?.series_name ? `, ${params.series_name}` : ''}</span><span class="extra-info">${aw_width}Wx${aw_height}H</span>`,
      HOOD: `${accessory.label}<span class="extra-info">${width}Wx${height}Hx${depth}D</span>`,
      DAMPER: `${accessory.label}<span class="extra-info">${params?.mfg_name ? `${params.mfg_name}` : ''}${params?.series_name ? `, ${params.series_name}` : ''}</span><span class="extra-info">${aw_width}Wx${aw_height}H</span>`,
      VFD: `${accessory.label}<span class="extra-info">${params?.mfg_name ? `${params.mfg_name}` : ''}${params?.series_name ? `, ${params.series_name}` : ''}</span> <span class="extra-info">${params?.power ? `${params.power} HP,` : ''} ${params?.voltage || ''}</span>`,
      JBOX: `${accessory.label}<span class="extra-info">${params?.mfg_name ? `${params.mfg_name}` : ''}</span><span class="extra-info">${width}Wx${height}H${params?.nema_encl_type ? `, ${params.nema_encl_type}` : ''}</span>`,
      ENCLOSURE: `${accessory.label}<span class="extra-info">${params?.mfg_name ? `${params.mfg_name}` : ''}${params?.series_name ? `, ${params.series_name}` : ''}</span>  <span class="extra-info">${width}Wx${height}Hx${depth}D${params?.nema_encl_type ? `, ${params.nema_encl_type}` : ''}</span>`,
      DISCONNECT: `${accessory.label}<span class="extra-info">${params?.mfg_name ? `${params.mfg_name}` : ''}${params?.series ? `, ${params.series}` : ''}</span><span class="extra-info">${params?.amp_rating ? `${params.amp_rating} Amp` : ''}${params?.amp_rating && params?.nema_encl_type_name ? ',' : ''} ${params?.nema_encl_type_name || ''}</span>`,
    };
    const html =
      accessory.name === 'AIRFLOW' && this.previewAccessoryId
        ? ''
        : accessories[accessory.name] ?? accessory.label;
    // jQuery `.html(undefined)` is a no-op getter — keep that exact semantic.
    if (html !== undefined) tooltip.innerHTML = html;
    tooltip.style.left = `${mouseEvent.offsetX + 19}px`;
    tooltip.style.top = `${mouseEvent.offsetY}px`;
    tooltip.classList.remove('hide');
    this.renderer.domElement.classList.add('cursor-pointer');
  }

  // [NEG]#3 — getSectionTooltip (canvas.view3d:862-875) is DEAD (TypeError on
  // first call, no live caller). Not ported.

  /**
   * onSectionAction (canvas.view3d:876-1095) — THE picking decision tree.
   * `event` is 'hover' or a MouseEvent ('click'/'dblclick' — legacy binds the
   * same handler to both, so a dblclick runs click,click,dblclick).
   * Raycast priority (§3.2.7): (1) airflow icons → (2) section cubes →
   * (3) accessory shapes → (4) lugs.
   */
  onSectionAction(event: 'hover' | MouseEvent, mouseEvent?: MouseEvent): void {
    if (this.previewAccessoryId != null && event !== 'hover') return;
    if (!mouseEvent) mouseEvent = event as MouseEvent;
    let clicked_on_section = false;
    // Read for the legacy dead branch below (see the [NEG] note there).
    const init_selected_sections = this.getSelectedSectionIds();
    void init_selected_sections;
    this.need_update = true;
    const isCtrlPressed = event !== 'hover' && (event as MouseEvent).ctrlKey;
    if (event !== 'hover') this.section_with_error = null;
    let color: number = event === 'hover' ? sectionMaterialColorHovered : sectionMaterialColorActive;
    let color_wireframe: number =
      event === 'hover' ? wireframeMaterialColorHovered : wireframeMaterialColorActive;
    // G34 note: legacy also derives dimensionColor (0x006834) here but never
    // applies it (dmount-frame recolors commented out) — not ported.
    const rect = this.renderer.domElement.getBoundingClientRect();
    const pointer = {
      x: ((mouseEvent.clientX - rect.left) / rect.width) * 2 - 1,
      y: -((mouseEvent.clientY - rect.top) / rect.height) * 2 + 1,
    };

    this.raycaster.setFromCamera(pointer as any, this.camera);
    // Calculate objects intersecting the picking ray.
    const cubes = this.sectionCanvasObjects.map((obj: any) => obj.cube);
    // dev-sync (canvas.view3d delta @902/@1115): VFD bank/enclosure
    // extraShapes are pickable too.
    const shapes = this.getAccessoryShapes();
    const lugs_children: any[] = [];
    Object.values(this.tunnels_lugs_on_canvas).forEach((e: any) => {
      if (e.type === 'Group') {
        lugs_children.push(e);
      } else {
        lugs_children.push(...e.children);
      }
    });
    const icons = Object.values(this.tunnelsAirflowIcons);
    const intersects_icons = this.raycaster.intersectObjects(icons as any[], true);
    const object: any = intersects_icons[0]?.object;

    const tunnel_id = object?.tunnel_id || object?.parent?.tunnel_id;
    if (event !== 'hover' && tunnel_id && !object.disabled) {
      // tunnels.get(id).toggle_airflow_direction() — §20.3 #21 PATCH via hook.
      this.onToggleAirflowDirection(tunnel_id);
      object.material.opacity = 0.3;
      object.disabled = true;
      if (object.sibling) {
        object.sibling.material.opacity = 0.3;
        object.sibling.disabled = true;
      }
      return;
    }
    if (event === 'hover' && tunnel_id) {
      const airflowLabel = object?.airflowLabel ?? object?.parent?.airflowLabel;
      this.getAccessoryTooltip({ name: 'AIRFLOW', airflowLabel }, mouseEvent);
      return;
    }
    const intersects = this.raycaster
      .intersectObjects(cubes, true)
      .filter(
        (el: any) =>
          el.object.shape_type === 'accessory' ||
          (el.object?.name && el.object?.parent?.type === 'Group' && el.object.parent?.parent?.type === 'Scene')
      );
    const intersects_accessory = this.raycaster
      .intersectObjects(shapes, true)
      .filter((el: any) => el.object.shape_type === 'accessory' && el.object.name !== 'cube_casing');
    const intersects_lug =
      this.raycaster
        .intersectObjects(lugs_children, true)
        .filter((el: any) => el.object?.parent?.shape_type === 'tunnel_lug')?.[0]?.object?.parent ||
      this.raycaster
        .intersectObjects(lugs_children, true)
        .filter((el: any) => el.object?.shape_type === 'tunnel_lug')?.[0]?.object;
    if (this.hoveredLug) {
      this.resetLugColor(this.hoveredLug);
      this.hoveredLug = null;
    }
    // G10 lug hover/select machine (Ctrl multi up to 2, same tunnel +
    // base_direction; emits change:active:lugs — parity 74). Lives in 6c2
    // geometry; click on a lug short-circuits the section logic.
    this.updateLugStyles(intersects_lug, event, isCtrlPressed);
    if (intersects_lug && event !== 'hover') return;
    let accessory_clicked = false;
    let accessory_clicked_section: any = null;

    const accesory_id = (intersects_accessory[0]?.object as any)?.accessory?.id;
    // Accessory-click only counts when its form row is collapsed or the
    // Accessories tab is inactive (§3.2.7) — DOM probes re-homed to hooks
    // (`li#forms_accessory_<id> input:not(:checked)…` /
    // `#show_accessories.active`).
    if (
      event !== 'hover' &&
      accesory_id &&
      (this.isAccessoryRowCollapsed(accesory_id) || this.getCurrentTab() !== 'accessories')
    ) {
      accessory_clicked = true;
      if (this.selectedAccessory && this.accessoryCanvasObjectsObj[this.selectedAccessory]) {
        this.changeAccessoryColor(
          this.accessoryCanvasObjectsObj[this.selectedAccessory],
          this.accessoriesWithError[this.selectedAccessory],
          false,
          true
        );
      }
    }
    if (this.hovered_accessory && this.accessoryCanvasObjectsObj[this.hovered_accessory])
      this.changeAccessoryColor(
        this.accessoryCanvasObjectsObj[this.hovered_accessory],
        this.accessoriesWithError[this.hovered_accessory],
        false
      );
    if (accesory_id && mouseEvent) {
      this.getAccessoryTooltip((intersects_accessory[0].object as any).accessory, mouseEvent);
      accessory_clicked_section = (intersects_accessory[0]?.object as any)?.accessory.section_id;
      this.hovered_accessory = accesory_id;
      if (this.accessoryCanvasObjectsObj[accesory_id])
        this.changeAccessoryColor(
          this.accessoryCanvasObjectsObj[accesory_id],
          this.accessoriesWithError[accesory_id],
          true
        );
    } else {
      this._tooltipEl()?.classList.add('hide');
      this.hovered_accessory = null;
    }
    this.sectionCanvasObjects.forEach((section: any) => {
      const el =
        section.cube.position.x === intersects[0]?.object.position.x &&
        section.cube.position.y === intersects[0]?.object.position.y &&
        section.cube.position.z === intersects[0]?.object.position.z;
      let accessory: any =
        section.cube.position.x === intersects[0]?.object?.parent?.position.x &&
        section.cube.position.y === intersects[0]?.object?.parent?.position.y &&
        section.cube.position.z === intersects[0]?.object?.parent?.position.z &&
        intersects_accessory[0];
      if (event !== 'hover' && !accessory && accessory_clicked_section == section.section.id)
        accessory = (intersects_accessory[0]?.object as any)?.accessory;
      if (el || accessory) {
        if (accessory_clicked_section != section.section.id) {
          this._tooltipEl()?.classList.add('hide');
          if (this.accessoryCanvasObjectsObj[accesory_id])
            this.changeAccessoryColor(
              this.accessoryCanvasObjectsObj[accesory_id],
              this.accessoriesWithError[accesory_id]
            );
        }
        if (this.section_with_error != section.section.id && !this.previewAccessoryId) {
          const colors = this.getSectionColors(section.section.id);
          color = section.active ? colors.selected : event === 'hover' ? colors.hover : colors.selected;
          color_wireframe = section.active ? wireframeMaterialColorActive : color_wireframe;
          section.cube.material.color.set(color);
          section.wireframe.material.color.set(color_wireframe);
          section.cube.viewports?.forEach((vp: any) => vp.material.color.set(color));
        }
        if (event !== 'hover') {
          clicked_on_section = true;
          // `#forms_accessories > div.accessories:not(.active) … :checked`
          // click — collapse checked rows of OTHER sections (hook).
          this.onCollapseAccessoryRows('others');
          if (!section.active || (mouseEvent as MouseEvent).type === 'dblclick') {
            // collapse ALL checked accessory rows.
            this.onCollapseAccessoryRows('all');
            const current_section_id = this.getCurrentSectionId();
            // Legacy reads `current_section.id` off an ID (`?.id` already
            // applied) → the inner OR is always truthy; the observable
            // condition is just `!!current_section` (§3.2.7 port note).
            if (current_section_id) {
              const sections = this.sectionCanvasObjects.filter((s: any) => s.active);
              if (!isCtrlPressed) {
                if (sections.length > 0) {
                  sections.forEach((act: any) => {
                    const colors = this.getSectionColors(act.section.id);
                    act.cube.material.color.set(colors.passive);
                    act.wireframe.material.color.set(wireframeMaterialColor);
                    act.cube.viewports?.forEach((vp: any) => vp.material.color.set(colors.passive));
                    act.active = false;
                  });
                }
              }
            }
            section.active = true;
            if (accessory_clicked && accessory_clicked_section == section.section.id && !isCtrlPressed) {
              this.emitBus('select:section', {
                section: section.section,
                tab: 'accessories',
                isCtrlPressed,
              });
              this.onShowAccessoriesTab(); // §13 row 4
              this.onExpandAccessoryRow(accesory_id); // §13 row 8
              if ((intersects_accessory[0]?.object as any)?.accessory.name === 'ACCESS_DOOR') {
                // §13 row 9 — ACCESS_DOOR auto-opens its mounting subtab
                // after 300 ms (observable defer preserved).
                const timer = window.setTimeout(() => {
                  this.onOpenAccessoryMountingSubtab(accesory_id);
                  clearTimeout(timer);
                }, 300);
              }
            } else
              this.emitBus('select:section', {
                section: section.section,
                isCtrlPressed,
                tab: isCtrlPressed ? 'sections' : '',
              });
            this.is_selected = true; // [NEG]#10 — scoped
            this.selected = section.id || section.section?.id;
            if ((mouseEvent as MouseEvent).type === 'dblclick') {
              const timer = window.setTimeout(() => {
                const accessoryName =
                  this.hovered_accessory &&
                  this.accessoryCanvasObjectsObj[this.hovered_accessory]?.shape?.name;
                if (accessoryName && ['HOOD', 'DAMPER', 'LOUVER', 'OPENING'].includes(accessoryName)) {
                  // §13 row 10 — the accessory's SELECTION dialog, unless
                  // rating mode (`.main-rotation.rotate` guard → boardMode
                  // slice). canvas.view3d.js:1023 clicks
                  // `#forms_accessory_<id> label.active-mode`, which is the
                  // form's "Select" button — 6d read it as the mounting popup.
                  if (!this.isRatingModeActive())
                    this.onOpenAccessorySelector(this.hovered_accessory as number);
                } else {
                  // Legacy's second branch probes `#forms_access_door_<id>
                  // .active-mode` (canvas.view3d.js:1024). No legacy template
                  // or view ever renders a `forms_access_door_*` element — it
                  // is the only reference to that id in the whole tree — so the
                  // branch never ran and ACCESS_DOOR fell through to the rating
                  // dialog below. Access doors have no Select heading either
                  // (access_door/tmpl/form.tmpl), so there is nothing to open.
                  // Dropped rather than re-homed: 6d re-pointed it at the
                  // mounting popup and made dead code observable.
                  //
                  // `$('#show_rating:not(.invisible-tab)').click()` — §13 row 6.
                  this.onOpenRating(false);
                }
                clearTimeout(timer);
              }, 200);
            }
          } else {
            if (accessory_clicked && accessory_clicked_section == section.section.id && !isCtrlPressed) {
              this.onShowAccessoriesTab(); // §13 row 4
              this.onExpandAccessoryRow(accesory_id); // §13 row 8
              if ((intersects_accessory[0]?.object as any)?.accessory.name === 'ACCESS_DOOR') {
                const timer = window.setTimeout(() => {
                  this.onOpenAccessoryMountingSubtab(accesory_id); // §13 row 9
                  clearTimeout(timer);
                }, 300);
              }
              return; // continue (forEach callback — same as legacy)
            }
            if (!isCtrlPressed) this.onCollapseAccessoryRows('all');
            // Click on already-active section — multi-aware deselect
            // (parity 67).
            const in_selected_sections = this.getSelectedSectionIds();
            const deselect =
              section.active && (in_selected_sections.length < 2 || isCtrlPressed) ? section : null;
            section.active = !deselect;
            let selected_ids: number[] = [];
            if (isCtrlPressed)
              selected_ids = in_selected_sections?.filter((id) => id != deselect?.section?.id) || [];
            else if (!deselect && in_selected_sections.length > 1)
              selected_ids = [section.section.id];
            if (deselect) {
              const colors = this.getSectionColors(section.section.id);
              section.cube.material.color.set(colors.passive);
              section.wireframe.material.color.set(wireframeMaterialColor);
              section.cube.viewports?.forEach((vp: any) => vp.material.color.set(colors.passive));
            }
            this.onViewdataSet({
              currentTab: selected_ids.length < 1 ? 'ahu' : 'sections',
              currentSectionId:
                selected_ids.length < 1 ? null : selected_ids[selected_ids.length - 1],
              selectedSectionIds: selected_ids,
            });
            this.onOpenFirstTunnelAccordion(); // §13 row 15
            this.emitBus('update:multiple:sections:list');
            if (selected_ids.length === 1) this.deselectAllActiveSections();
          }
        }
        this.renderer.domElement.classList.add('cursor-pointer');
      } else if (!section.active && event === 'hover') {
        const colors = this.getSectionColors(section.section.id);
        section.cube.material.color.set(colors.passive);
        section.wireframe.material.color.set(wireframeMaterialColor);
        section.cube.viewports?.forEach((vp: any) => vp.material.color.set(colors.passive));
        section.active = false;
      } else if (section.active && event === 'hover') {
        const colors = this.getSectionColors(section.section.id);
        section.wireframe.material.color.set(colors.selected);
      }
    });
    if (event !== 'hover') {
      const selected_sections = this.getSelectedSectionIds();
      const reselect = !!(selected_sections.length > 1 && !clicked_on_section);
      // [legacy quirk] the first branch compares the selected-sections ARRAY
      // against 1 (`init_selected_sections > 1`) → string coercion → NaN > 1
      // → ALWAYS false. Only the `reselect` branch is live: click on empty
      // space with a multi-select clears all (parity 66).
      if (reselect) {
        this.onViewdataSet({ currentTab: 'ahu', currentSectionId: null, selectedSectionIds: [] });
        this.deselectAllActiveSections();
      }
    }
  }

  /**
   * onRightClick (canvas.view3d:1096-1175) — hit typing for the context
   * menus: accessory beats section only when it belongs to it (or floats
   * free); returns `{type:'accessory'|'section'|'empty', data, position}`.
   */
  onRightClick(
    mouseEvent: MouseEvent
  ): { type: 'accessory' | 'section' | 'empty'; data?: any; position: { x: number; y: number } } | undefined {
    if (!mouseEvent) return undefined;

    const rect = this.renderer.domElement.getBoundingClientRect();
    const pointer = {
      x: ((mouseEvent.clientX - rect.left) / rect.width) * 2 - 1,
      y: -((mouseEvent.clientY - rect.top) / rect.height) * 2 + 1,
    };

    this.raycaster.setFromCamera(pointer as any, this.camera);

    // prepare objects
    const cubes = this.sectionCanvasObjects.map((obj: any) => obj.cube);
    // dev-sync (canvas.view3d delta @902/@1115): VFD bank/enclosure
    // extraShapes are pickable too.
    const shapes = this.getAccessoryShapes();
    let section: any = null;
    let accessory: any = null;

    // detect accessory
    const intersects_accessory = this.raycaster
      .intersectObjects(shapes, true)
      .filter((el: any) => el.object.shape_type === 'accessory' && el.object.name !== 'cube_casing');

    if (intersects_accessory.length > 0) {
      accessory = (intersects_accessory[0].object as any).accessory;
    }

    // detect section
    const intersects_section = this.raycaster.intersectObjects(cubes, true);

    if (intersects_section.length > 0) {
      const clicked = intersects_section[0].object;
      section = this.sectionCanvasObjects.find(
        (s: any) =>
          s.cube.position.x === clicked.position.x &&
          s.cube.position.y === clicked.position.y &&
          s.cube.position.z === clicked.position.z
      );
    }
    if ((accessory && !section) || (accessory && accessory.section_id == section?.section?.id))
      return {
        type: 'accessory',
        data: accessory,
        position: { x: mouseEvent.offsetX, y: mouseEvent.offsetY },
      };
    if (section) {
      return {
        type: 'section',
        data: section.section,
        position: { x: mouseEvent.offsetX, y: mouseEvent.offsetY },
      };
    }

    // empty space
    return { type: 'empty', position: { x: mouseEvent.offsetX, y: mouseEvent.offsetY } };
  }

  /**
   * openAccessoryMenu (canvas.view3d:5742-5755) — heading = label before ':';
   * buttons get data-id/data-type/data-section; position +19px x (parity 71).
   * The button click handlers are React's (BoardContent — §13 row 12).
   */
  openAccessoryMenu(result: any): void {
    const menu = document.getElementById('accessory-context-menu');
    if (!menu) return;
    menu.style.left = `${result.position.x + 19}px`;
    menu.style.top = `${result.position.y}px`;
    const label = result.data.label;
    const index = label?.indexOf(':') ?? -1;
    const resultText = index !== -1 ? label.substring(0, index).trim() : label;
    const heading = menu.querySelector('div.heading');
    if (heading) heading.innerHTML = `${resultText}`;
    menu.querySelectorAll('button').forEach((btn) => {
      btn.setAttribute('data-id', String(result.data.id));
      // jQuery attr(name, undefined) is a no-op — keep that semantic.
      if (result.data.type !== undefined) btn.setAttribute('data-type', String(result.data.type));
      if (result.data.section_id !== undefined)
        btn.setAttribute('data-section', String(result.data.section_id));
    });
    menu.classList.remove('hide');
    this.renderer.domElement.classList.add('cursor-pointer');
  }

  /** openSectionMenu (canvas.view3d:5756-5765) — heading `tag||name + " Section"`. */
  openSectionMenu(result: any): void {
    const menu = document.getElementById('section-context-menu');
    if (!menu) return;
    menu.style.left = `${result.position.x + 19}px`;
    menu.style.top = `${result.position.y}px`;
    const heading = menu.querySelector('div.heading');
    if (heading)
      heading.innerHTML = `${result.data.get('tag') || result.data.get('name')} Section`;
    menu.querySelectorAll('button').forEach((btn) => {
      btn.setAttribute('data-id', String(result.data.id));
      // legacy reads `.type` off the Backbone MODEL instance (not an attr) →
      // undefined → jQuery attr() no-op; the wrapper's `.type` is equally
      // undefined, so the attribute is never written (verbatim-observable).
      const type = (result.data as any).type;
      if (type !== undefined) btn.setAttribute('data-type', String(type));
    });
    // sync K (canvas.view3d delta @6227-6228): with a multi-select active the
    // section Duplicate action is unavailable — toggle `.non-clickable`
    // (pointer-events:none in _general.scss, so the React onClick can't fire
    // either). Computed at open time, both directions, section menu only.
    const multiple_sections_selected = (this.getSelectedSectionIds()?.length ?? 0) > 1;
    menu.querySelectorAll('button[name="duplicate"]').forEach((btn) => {
      btn.classList.toggle('non-clickable', multiple_sections_selected);
    });
    menu.classList.remove('hide');
    this.renderer.domElement.classList.add('cursor-pointer');
  }

  /**
   * closeContextMenu (canvas.view3d:5766-5770) — also registered on window
   * `click` (any click closes the menus). Arrow fn: the legacy registers it
   * UNBOUND (its body only touches jQuery globals); ours touches `this`.
   * Verbatim quirk kept: closing ADDS `cursor-pointer` to the canvas.
   */
  closeContextMenu = (): void => {
    document.querySelectorAll('.context-menu').forEach((m) => m.classList.add('hide'));
    document.querySelectorAll('.context-menu div.heading').forEach((h) => {
      h.innerHTML = '';
    });
    this.renderer?.domElement?.classList.add('cursor-pointer');
  };

  /** viewdata `current_section` (a model in legacy) → live wrapper resolve. */
  private resolveCurrentSectionWrapper(): any {
    const id = this.getCurrentSectionId();
    if (id == null) return null;
    for (const list of Object.values(this.sectionsByTunnel)) {
      const found = (list as any[]).find((s: any) => s.id == id);
      if (found) return found;
    }
    return null;
  }

  /**
   * keyDown (canvas.view3d:349-378) — parity 75:
   *  - Enter (13): open rating unless resolution-apply visible / popup open /
   *    multi-select (guards: the error-log + popup probes stay DOM — their
   *    owners are shell/6e nodes; rating-mode guard reads the boardMode slice).
   *  - ←/→ (37/39): section at order ±1 in the same tunnel, else first/last
   *    section of next/prev tunnel (wraps).
   *  - ↑/↓ (38/40): first section (order 1) of next/prev tunnel (wraps).
   *  - Ctrl held → toggle membership in selected_sections.
   * Typing inside #forms-main-container never reaches this (6b stopPropagation
   * guard, canvas.view3d:326).
   */
  keyDown(e: KeyboardEvent): void {
    if (this.previewAccessoryId != null) return;
    const isCtrlPressed = e.ctrlKey;
    if (!isCtrlPressed) this.last_pressed_section = null;
    const current_section = this.last_pressed_section || this.resolveCurrentSectionWrapper();
    const multiply_sections_selected = (this.getSelectedSectionIds()?.length ?? 0) > 1;
    if (!current_section || this.isRatingModeActive()) return;
    const tunnel = current_section.tunnel_id;
    const tunnel_index = this.tunnelModels.findIndex((el: any) => el.id == tunnel);
    if (
      e.keyCode == 13 &&
      !document.querySelector(
        '#error_log:not(.opacity_0) #resolve-error-log-wrapper.actived #resolution-apply:not(.invisible)'
      )
    ) {
      if (!document.querySelector('.popup-wrapper.active') && !multiply_sections_selected)
        this.onOpenRating(true); // `#show_rating:not(.invisible-tab):not(.active)` — §13 row 6
    } else if (e.keyCode == 39 || e.keyCode == 37) {
      const order = e.keyCode == 39 ? current_section.order + 1 : current_section.order - 1;
      let section = this.sectionsByTunnel?.[tunnel]?.find((el: any) => el.order === order);
      if (!section) {
        const prev_idx = tunnel_index ? tunnel_index - 1 : this.tunnelModels.length - 1;
        const next_idx = tunnel_index + 1;
        const new_tunnel = (
          e.keyCode == 39
            ? this.tunnelModels[next_idx] || this.tunnelModels[0]
            : this.tunnelModels[prev_idx]
        )?.id;
        section = this.sectionsByTunnel?.[new_tunnel as number]?.find(
          (el: any) =>
            el.order === (e.keyCode == 39 ? 1 : this.sectionsByTunnel?.[new_tunnel as number]?.length)
        );
      }
      this.selectOrdeselectSectiononKeyDown(section, isCtrlPressed);
    } else if (e.keyCode == 38 || e.keyCode == 40) {
      const prev_idx = tunnel_index ? tunnel_index - 1 : this.tunnelModels.length - 1;
      const next_idx = tunnel_index + 1;
      const new_tunnel = (
        e.keyCode == 38
          ? this.tunnelModels[next_idx] || this.tunnelModels[0]
          : this.tunnelModels[prev_idx]
      )?.id;
      const section = this.sectionsByTunnel?.[new_tunnel as number]?.find(
        (el: any) => el.order === 1
      );
      this.selectOrdeselectSectiononKeyDown(section, isCtrlPressed);
    }
  }

  /**
   * selectOrdeselectSectiononKeyDown (canvas.view3d:379-417): Ctrl toggles
   * membership (dedupe + restyle + `update:multiple:sections:list`); plain
   * arrows select (`select:section {section, isCtrlPressed}` + restyle).
   */
  selectOrdeselectSectiononKeyDown(section: any, isCtrlPressed: boolean): void {
    if (!section) return;

    let select = false;
    if (!isCtrlPressed) select = true;
    else {
      this.last_pressed_section = section;
      const selectedIds = this.getSelectedSectionIds();
      const isAlredySelected = selectedIds.find((id) => id == section.id);
      if (!isAlredySelected) select = true;
      else {
        const selected_ids = selectedIds.filter((id) => id != section.id);
        this.onViewdataSet({
          currentTab: selected_ids.length < 1 ? 'ahu' : 'sections',
          currentSectionId:
            selected_ids.length < 1 ? null : selected_ids[selected_ids.length - 1],
          selectedSectionIds: selected_ids,
        });
        const sec_on_canvas = this.sectionCanvasObjects.find(
          (s: any) => s.section.id == section.id
        );
        this.updateSectionStyles(sec_on_canvas, false);
        this.emitBus('update:multiple:sections:list');
      }
    }
    if (select) {
      this.emitBus('select:section', { section, isCtrlPressed });
      this.selectDeselectSection(section.id, false, true, '', isCtrlPressed);
    }
  }
}

// ---------------------------------------------------------------------------
// ACCESSORY SELECTOR MINI-3D PREVIEW — factory (fn['create.view.canvas.for.
// accessory'], fn/map.js:1137-1151). Moved here from canvas3dGeometry.ts
// (Phase 6e2c-3) so the preview can be a real Canvas3DInteraction rather than
// a bare Canvas3DGeometry: the legacy accessory selector's mini-3D canvas is
// the SAME canvas.view3d.js View class as the board, just re-parented with
// previewAccessoryId set, so it keeps hover-tooltip + drag-rotate for free
// (see attachHoverInteraction above). The interaction-only hooks below are
// reused directly off the live instance — they're only there so
// getAccessoryTooltip's ADP_GAGE branch can resolve real mfg names; the
// click/keyboard-only hooks (onOpenAccessorySelector etc.) are never invoked
// because attachHoverInteraction never wires click/dblclick/keydown.
// ---------------------------------------------------------------------------

export interface AccessoryPreviewCanvasOptions {
  /** The popup's canvas-host element (sizing contract: (offset−20)/12.5, §3.1). */
  el: HTMLElement;
  /** A FRESH <canvas> owned by the popup (the renderer binds to it). */
  canvas: HTMLCanvasElement;
  /** The accessory being staged — drives previewAccessoryId / colorless mode / coef cap 0.35. */
  accessoryId: number;
}

export function createAccessoryPreviewCanvas(
  live: Canvas3DInteraction,
  { el, canvas, accessoryId }: AccessoryPreviewCanvasOptions
): Canvas3DInteraction {
  const preview = new Canvas3DInteraction({
    el,
    canvas,
    font: live.font,
    showColor: false, // preview canvas always colorless (§3.2.3 / parity 89)
    getTunnels: live.getTunnels,
    hooks: {
      // The preview owns no board chrome — pills/breadcrumb stay the live
      // canvas's (the legacy preview view also re-renders #tunnels-indexes,
      // a shared-DOM side effect the React board intentionally keeps scoped
      // to the live canvas).
      onViewChange: () => undefined,
      onActiveTunnelIndexChange: () => undefined,
      onTunnelsIndexesVisible: () => undefined,
    },
    previewAccessoryId: accessoryId,
    getSelectedSectionIds: live.getSelectedSectionIds,
    getFloorConstructionId: live.getFloorConstructionId,
    emitBus: live.emitBus,
    onRotateAvailabilityClear: () => undefined,
    getTunnelLugs: live.getTunnelLugs,
    getCurrentSectionId: live.getCurrentSectionId,
    isAccessoryRowCollapsed: live.isAccessoryRowCollapsed,
    isAccessoriesTabActive: live.isAccessoriesTabActive,
    hasErrorLogRowForSection: live.hasErrorLogRowForSection,
    // interaction-only hooks (Canvas3DInteractionOptions) — required to
    // construct, but dead in preview mode: onSectionAction/onRightClick/
    // keyDown all early-return non-hover events once previewAccessoryId is
    // set, and attachHoverInteraction never wires click/dblclick/keydown.
    getCurrentTab: live.getCurrentTab,
    isRatingModeActive: live.isRatingModeActive,
    onViewdataSet: live.onViewdataSet,
    onShowAccessoriesTab: live.onShowAccessoriesTab,
    onExpandAccessoryRow: live.onExpandAccessoryRow,
    onOpenAccessoryMountingSubtab: live.onOpenAccessoryMountingSubtab,
    onOpenAccessorySelector: live.onOpenAccessorySelector,
    onOpenRating: live.onOpenRating,
    onCollapseAccessoryRows: live.onCollapseAccessoryRows,
    onOpenFirstTunnelAccordion: live.onOpenFirstTunnelAccordion,
    onToggleAirflowDirection: live.onToggleAirflowDirection,
    getAdpGageMfgName: live.getAdpGageMfgName,
  });
  // Cache hand-off (fn/map.js:1142-1148 clone semantics + tunnel.js:81-84).
  preview.accessoryCanvasObjects = [...live.accessoryCanvasObjects];
  preview.coilConnections = { ...live.coilConnections };
  preview.sections_cassing = { ...live.sections_cassing };
  preview.int_wall = {
    vertical: { ...live.int_wall.vertical },
    horizontal: { ...live.int_wall.horizontal },
  };
  // NB evapPackConnections is NOT cloned — verbatim fn-map omission (evap
  // piping absent from the legacy preview too).
  preview.render_canvas(); // tunnel.js init :93
  return preview;
}
